import { database } from "./database";
import { BaseFirestore, CreateTenantDto, Role, Tenant } from "@/models";
import { Collections } from "@/types/Collections";
import { arrayRemove, arrayUnion } from "@/lib/utils.ts";
import { httpsCallables } from "@/repositories/httpsCallables.ts";
import { technician } from "@/repositories/technician.ts";
import { user } from "@/repositories/user.ts";

export const tenant = {
  getTenantsByCode: async (joinCode: string) => {
    return database.queryDocuments<Tenant>(
      Collections.tenants,
      "joinCode",
      "==",
      joinCode
    );
  },
  get: (id: string) => {
    return database.get<Tenant>(id, Collections.tenants);
  },
  listTenantsByUser: async (userId: string) => {
    return database.queryDocuments<Tenant>(
      Collections.tenants,
      "userIds",
      "array-contains",
      userId
    );
  },
  listTenants: async () => {
    return database.list<Tenant>(Collections.tenants);
  },
  subscribeTenant: async (
    id: string,
    callback: (doc: Tenant | undefined) => void
  ) => {
    return database.subscribeDocument<Tenant>(
      id,
      Collections.tenants,
      callback
    );
  },
  update: async (id: string, data: Partial<Tenant>) => {
    return database.update({ id: id, ...data }, `${Collections.tenants}/${id}`);
  },
  addUserViaTechnician: async (
    tenantId: string,
    userId: string,
    role: Role,
    email: string
  ) => {
    const tenantData = await tenant.get(tenantId);

    if (!tenantData) {
      throw new Error("Could not find tenant");
    }

    await database.create(
      { id: userId, role: role, email: email ?? "" },
      `${Collections.tenants}/${tenantId}/${Collections.users}`
    );

    if (role === "superAdmin" || role === "admin") {
      await httpsCallables.updateUserClaims(
        userId,
        true,
        tenantId,
        tenantData.joinCode
      );
      return tenant.update(tenantId, {
        adminUserIds: arrayUnion(userId, tenantData.adminUserIds),
      });
    }

    await httpsCallables.updateUserClaims(
      userId,
      false,
      tenantId,
      tenantData.joinCode
    );
    return tenant.update(tenantId, {
      userIds: arrayUnion(userId, tenantData.userIds),
    });
  },
  addUser: async (
    tenantId: string,
    userId: string,
    role: Role,
    joinCode: string
  ) => {
    const tenantData = await tenant.get(tenantId);
    const userData = await user.getById(userId);

    if (!tenantData) {
      throw new Error("Could not find tenant");
    }
    if (!userData) {
      throw new Error("Could not find user");
    }

    if (tenantData.joinCode !== joinCode) {
      throw new Error("Could not join tenant with code provided");
    }

    const technicianData = await technician.getByEmail(
      userData.email ?? "",
      tenantId
    );

    await httpsCallables.updateUserClaims(
      userId,
      role === "superAdmin" || role === "admin",
      tenantId,
      joinCode
    );

    await database.create(
      { id: userId, role: role, email: userData.email ?? "" },
      `${Collections.tenants}/${tenantId}/${Collections.users}`
    );

    if (technicianData) {
      await technician.update(
        technicianData.id,
        { linkedUserId: userId },
        tenantId
      );
    } else {
      await technician.create(
        {
          firstName: userData.firstName,
          lastName: userData.lastName,
          email: userData.email ?? "",
          tenantId: tenantId,
          displayName: `${userData.firstName} ${userData.lastName}`,
          role: role,
        },
        tenantId
      );
    }

    if (role === "superAdmin" || role === "admin") {
      return tenant.update(tenantId, {
        adminUserIds: arrayUnion(userId, tenantData.adminUserIds),
      });
    }

    return tenant.update(tenantId, {
      userIds: arrayUnion(userId, tenantData.userIds),
    });
  },
  removeUser: async (tenantId: string, userId: string) => {
    const tenantData = await tenant.get(tenantId);

    if (!tenantData) {
      throw new Error("Could not find tenant");
    }

    await database.delete(
      userId,
      `${Collections.tenants}/${tenantId}/${Collections.users}`
    );

    return tenant.update(tenantId, {
      adminUserIds: arrayRemove(userId, tenantData.adminUserIds),
      userIds: arrayRemove(userId, tenantData.userIds),
    });
  },
  create: async (tenantData: CreateTenantDto, userId?: string) => {
    const tenantJoinCode = createTenantJoinCode();
    const newTenant: Omit<Tenant, keyof BaseFirestore> = {
      ...tenantData,
      ownerUserId: userId ?? "",
      adminUserIds: userId ? [userId] : [],
      userIds: userId ? [userId] : [],
      disabled: false,
      joinCode: tenantJoinCode,
    };
    const createdTenant = await database.create<Tenant>(
      newTenant,
      Collections.tenants
    );
    if (userId) {
      await tenant.addUser(createdTenant.id, userId, "admin", tenantJoinCode);
      await httpsCallables.updateUserClaims(userId, true, createdTenant.id);
    }
    return createdTenant;
  },
};

export const createTenantJoinCode = () => {
  const getRandomChar = () =>
    Math.random().toString(36).substring(2, 3).toUpperCase();

  const generateSection = () =>
    Array.from({ length: 4 }, getRandomChar).join("");

  return `${generateSection()}-${generateSection()}`;
};
